import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// Store load timestamp when the module is first loaded
const pageLoadTime = Date.now()

// Keep track of keys managed by usePersistedState
const PERSISTED_KEYS_KEY = 'persisted_state_keys'
const LAST_PATH_KEY = 'last_base_path'

// Update the timestamp in sessionStorage only once when the module loads
if (sessionStorage.getItem('last_load_time') !== pageLoadTime.toString()) {
    const isPageReload = sessionStorage.getItem('last_load_time') !== null
    if (isPageReload) {
        // Clear only the persisted state keys
        const persistedKeys = JSON.parse(sessionStorage.getItem(PERSISTED_KEYS_KEY) || '[]')
        persistedKeys.forEach(key => localStorage.removeItem(key))
    }
    sessionStorage.setItem('last_load_time', pageLoadTime.toString())
}

// Helper to get base path without parameters
const getBasePath = (path) => {
    return '/' + path.split('/')[1] // Get first part of path (e.g., /users from /users/123)
}

export const usePersistedState = (key, defaultValue) => {
    const location = useLocation()
    const [value, setValue] = useState(() => {
        const storedValue = localStorage.getItem(key)
        return storedValue ? JSON.parse(storedValue) : defaultValue
    })

    // Register this key as being managed by usePersistedState
    useEffect(() => {
        const persistedKeys = JSON.parse(sessionStorage.getItem(PERSISTED_KEYS_KEY) || '[]')
        if (!persistedKeys.includes(key)) {
            persistedKeys.push(key)
            sessionStorage.setItem(PERSISTED_KEYS_KEY, JSON.stringify(persistedKeys))
        }
    }, [key])

    // Track navigation changes and clear state when base path changes
    useEffect(() => {
        const basePath = getBasePath(location.pathname)
        const lastPath = sessionStorage.getItem(LAST_PATH_KEY)

        if (lastPath && lastPath !== basePath) {
            // Clear all persisted states when base path changes
            const persistedKeys = JSON.parse(sessionStorage.getItem(PERSISTED_KEYS_KEY) || '[]')
            persistedKeys.forEach(k => {
                localStorage.removeItem(k)
                if (k === key) {
                    setValue(defaultValue)
                }
            })
        }

        sessionStorage.setItem(LAST_PATH_KEY, basePath)
    }, [location.pathname, key, defaultValue])

    // Update localStorage when value changes
    useEffect(() => {
        localStorage.setItem(key, JSON.stringify(value))
    }, [key, value])

    return [value, setValue]
} 